import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { BusinessInfo, ServiceType } from '@/types/api';

const SITE_URL = 'https://staging.thelaundryco.online';
const SCRIPT_ID = 'schema-org-ld-json';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

function pad2(n: number): string { return String(n).padStart(2, '0'); }
function hourStr(h: number): string { return `${pad2(h % 24)}:00`; }

/**
 * OpeningHoursSpecification, one entry per (day, window) pair -- a day
 * with a lunch-break split (e.g. 08:00-12:00, 16:00-22:00) needs two
 * entries, not one entry with a misleading single open/close pair.
 * Reads the real weekly schedule from GET /catalog/business-info; never
 * touches ramadan/holiday/exception overrides here -- those are date-
 * scoped and schema.org's spec has no clean way to express "this
 * recurring rule doesn't apply next Tuesday," so the baseline weekly
 * schedule is what's published, same tradeoff every static JSON-LD
 * generator for a business with seasonal hours makes.
 */
function openingHoursSpecification(weekly: BusinessInfo['hours']['weekly']) {
  const specs: Record<string, unknown>[] = [];
  weekly.forEach((day, i) => {
    if (day.closed || !day.windows?.length) return;
    for (const w of day.windows) {
      specs.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: `https://schema.org/${DAY_NAMES[i]}`,
        opens: hourStr(w.start_hour),
        closes: hourStr(w.end_hour),
      });
    }
  });
  return specs;
}

function serviceEntries(services: ServiceType[]) {
  return services
    .filter((s) => s.is_active)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((s) => {
      const service: Record<string, unknown> = {
        '@type': 'Service',
        name: s.name?.en,
        ...(s.description?.en ? { description: s.description.en } : {}),
        areaServed: { '@type': 'Country', name: 'Bahrain' },
        provider: { '@id': `${SITE_URL}/#business` },
      };
      // Only a service with a single, unambiguous real price (flat_rate)
      // gets an Offer -- per-item pricing varies by garment, and inventing
      // a representative number would be exactly the drift this markup
      // exists to avoid.
      if (s.pricing_mode === 'flat_rate' && s.flat_rate_fils != null) {
        service.offers = {
          '@type': 'Offer',
          price: (s.flat_rate_fils / 1000).toFixed(3),
          priceCurrency: 'BHD',
        };
      }
      return service;
    });
}

/**
 * Injects the site's LocalBusiness JSON-LD (Phase 20) -- generated from
 * live GET /catalog/business-info + GET /catalog/services, both public,
 * so the markup can never drift from what's actually configured in
 * Settings/the catalog. Mounted once in MarketingLayout, present on
 * every public page rather than duplicated per-route.
 */
export function SchemaOrg() {
  const infoQuery = useQuery({
    queryKey: ['seo', 'business-info'],
    queryFn: () => api<BusinessInfo>('/catalog/business-info'),
    staleTime: 10 * 60_000,
  });
  const servicesQuery = useQuery({
    queryKey: ['seo', 'services'],
    queryFn: () => api<{ services: ServiceType[] }>('/catalog/services'),
    staleTime: 10 * 60_000,
  });

  const info = infoQuery.data;
  const services = servicesQuery.data?.services;

  useEffect(() => {
    if (!info) return;

    const coords = info.facility?.location?.coordinates; // [lng, lat]
    const business: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#business`,
      name: info.business_name?.en,
      url: SITE_URL,
      ...(info.contact_phone ? { telephone: info.contact_phone } : {}),
      ...(info.contact_email ? { email: info.contact_email } : {}),
      ...(info.facility?.address ? {
        address: { '@type': 'PostalAddress', streetAddress: info.facility.address, addressLocality: 'Manama', addressCountry: 'BH' },
      } : {
        address: { '@type': 'PostalAddress', addressCountry: 'BH' },
      }),
      ...(coords ? { geo: { '@type': 'GeoCoordinates', latitude: coords[1], longitude: coords[0] } } : {}),
      openingHoursSpecification: openingHoursSpecification(info.hours.weekly),
      ...(info.aggregate_rating.value != null ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: info.aggregate_rating.value,
          reviewCount: info.aggregate_rating.count,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
      ...(services && services.length > 0 ? { makesOffer: serviceEntries(services) } : {}),
    };

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(business);

    // Left in place on unmount -- every marketing route wants the same
    // markup, and MarketingLayout (this component's only mount point)
    // never unmounts between two marketing routes, only when leaving the
    // marketing shell entirely (admin/driver/supervisor/login), where a
    // stale LocalBusiness tag sitting unused in <head> is harmless.
  }, [info, services]);

  return null;
}
